import { Icon } from 'Components/shared/Icon';
import { PopOverMenu } from 'Components/shared/PopOverMenu';
import localforage from 'localforage';
import React, { useState, useEffect, useCallback } from 'react';
import { STORAGE_KEY } from 'Services/voipMonitorService/adapters/voipQualityAdapter/index.constants';
import { CONNECTION_STATUS_MAP, POOR_CONNECTION_POPOVER_MESSAGE } from './index.constants';
import { Styled } from './index.styles';
import { CallConnectionStatusProps } from './index.types';

const COMPONENT_ALIAS = 'connection-status';

/**
 * CallConnectionStatus component – displays the current call connection status visually.
 *
 * Displays different icons based on connection quality:
 * - stable: 3 green bars ("connection-stable")
 * - unstable: 2 orange bars with "Weak connection" tooltip
 * - poor: 1 red bar with "Poor connection" tooltip and auto-show popover
 * - off: empty bars with "No connection" tooltip
 *
 * When in "poor" state, clicking the icon will toggle a popover that remains visible
 * until clicked again or clicked outside.
 *
 * @param {CallConnectionStatusProps} props - The component props
 * @param {CallConnectionState} props.status - Current connection state ('stable'|'unstable'|'poor'|'off')
 * @returns {React.ReactElement} The CallConnectionStatus component
 */

export const CallConnectionStatus = ({
  status = 'off',
  showText = true,
  testID,
  callId,
}: CallConnectionStatusProps): React.ReactElement => {
  const [showPopover, setShowPopover] = useState(false);

  useEffect(() => {
    const handlePoorConnection = async () => {
      if (callId && status === 'poor') {
        const shownCalls =
          (await localforage.getItem<string[]>(STORAGE_KEY)) || [];

        if (!shownCalls.includes(callId)) {
          // Store this callId to prevent future automatic popover displays
          await localforage.setItem(STORAGE_KEY, [...shownCalls, callId]);

          // Show popover for 5 seconds
          setShowPopover(true);
          setTimeout(() => {
            setShowPopover(false);
          }, 5000);
        }
      }
    };

    void handlePoorConnection();
  }, [callId, status]);

  const componentTestId = testID
    ? `${testID}-${COMPONENT_ALIAS}-${status}`
    : `${COMPONENT_ALIAS}-${status}`;

  const connectionStatusText = CONNECTION_STATUS_MAP[status].label;

  const handleTriggerClick = useCallback(() => {
    if (status === 'poor') {
      setShowPopover((prev) => !prev);
    }
  }, [status]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if ((event.key === 'Enter' || event.key === ' ') && status === 'poor') {
        handleTriggerClick();
      }
    },
    [handleTriggerClick, status]
  );

  return (
    <PopOverMenu
      show={showPopover}
      offset={[10, 10]}
      content={
        <Styled.PopOverMenuContent role="tooltip">
          <Styled.PopOverMenuMessage>
            {POOR_CONNECTION_POPOVER_MESSAGE}
          </Styled.PopOverMenuMessage>
        </Styled.PopOverMenuContent>
      }
      onOutsideClick={() => setShowPopover(false)}
      position="bottom right"
      data-testid={`${componentTestId}-popover`}
      trigger={
        <Styled.Container
          data-testid={`${componentTestId}-container`}
          $isClickable={status === 'poor'}
          onClick={handleTriggerClick}
          onKeyDown={handleKeyPress}
          role="status"
          aria-label={CONNECTION_STATUS_MAP[status].ariaLabel}
          tabIndex={status === 'poor' ? 0 : -1}
        >
          {showText && connectionStatusText && (
            <Styled.Label data-testid={`${componentTestId}-label`}>
              {connectionStatusText}
            </Styled.Label>
          )}
          {status && (
            <Icon
              icon={`connection-${status}`}
              data-testid={`${componentTestId}-icon`}
              aria-hidden="true"
            />
          )}
        </Styled.Container>
      }
    />
  );
};
